import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { useParams } from '../../../../contexts/RouterContext';
import { Icon, icons, RouterLink } from '../../../generic';
import type { SnippetLicense } from '../../../../../shared/snippets';
import type { RevisionRouteParams } from '../../projectDashboardTypes';

import DetailsSection from './DetailsSection';

import styles from './Details.module.less';

/**
 * Strip off anything after the revision id in the URL
 * and append the path to the project issues page for the given category.
 */
function getIssueURL({
  currentPath,
  category,
  issueId,
  revisionId,
}: {
  currentPath: string;
  category: string;
  issueId: number;
  revisionId: string;
}): string {
  const parts = currentPath.split('/');
  const revisionIndex = parts.indexOf(revisionId);
  const base = parts.slice(0, revisionIndex + 1).join('/');
  return `${base}/issues/${category}/${issueId}`;
}

const LicenseStatusUnlicensed = (
  <div className={styles.licenseStatusIcon}>
    <span className={styles.licenseStatusIconUnlicensed}>
      <Icon icon={icons.waveSine} />
    </span>
    Unlicensed
  </div>
);

const LicenseStatusDenied = (
  <div className={styles.licenseStatusIcon}>
    <span className={styles.licenseStatusIconDenied}>
      <Icon icon={icons.xMark} />
    </span>
    Denied
  </div>
);

const LicenseStatusFlagged = (
  <div className={styles.licenseStatusIcon}>
    <span className={styles.licenseStatusIconFlagged}>
      <Icon icon={icons.flagOutline} />
    </span>
    Flagged
  </div>
);

type Props = {
  licenses: SnippetLicense[];
};

/**
 * The Licenses section of the drawer details.
 */
const SnippetDrawerLicenses: VoidFunctionComponent<Props> = ({ licenses }) => {
  const { revision_id: revisionId } = useParams<RevisionRouteParams>();
  return (
    <DetailsSection heading="Licenses">
      {!licenses.length ? (
        <div className={styles.licensesNotFound}>
          No licenses found
        </div>
      ) : licenses.map(license => (
        <div className={styles.licensesRow} key={license.signature}>
          <span className={styles.licensesRowBold}>
            {license.signature}
          </span>
          <span className={styles.licensesRowBold}>
            {_.upperFirst(license.type)}
          </span>
          {license.status === 'unknown' && (
            LicenseStatusUnlicensed
          )}
          {license.status === 'denied' && (
            LicenseStatusDenied
          )}
          {license.status === 'flagged' && (
            LicenseStatusFlagged
          )}
          {license.issueId ? (
            <RouterLink to={getIssueURL({
              currentPath: window.location.pathname,
              category: 'licensing',
              issueId: license.issueId,
              revisionId,
            })}>
              View Issue
            </RouterLink>
          ) : <span />}
        </div>
      ))}
    </DetailsSection>
  );
};

export default SnippetDrawerLicenses;
